import {createApi} from "@reduxjs/toolkit/query/react"
import baseQuery from "../../utils/apiIIKOConfig"
import {IBasicResponse} from "../../types/api"
import {
    ICategoryUpdateParams,
    ICategoryRecommendation,
    ICategoryRecommendationCreateParams,
    ICategoryRecommendationDeleteParams,
    ICategoryRecommendationProductPositionUpdateParams,
    ICategoryRecommendationUpdateParams,
    ICombo,
    IComboByIdParams,
    IComboCommon,
    IComboItemProductPositionUpdateParams,
    IComboItemProductUpdateParams,
    IComboItemUpdateParams,
    IComboListParams,
    IComboUpdateParams,
    IMenuParams,
    IMenusResponse,
    IModifierUpdateParams,
    IModifierGroupUpdateParams,
    IPositionUpdateResponse,
    IProductGiftableUpdateParams,
    IProductPacketUpdateParams,
    IProductPositionUpdateParams,
    IProductServiceFeeUpdateParams,
    IProductShippingUpdateParams,
    IProductUpdateParams,
    IProductSizeUpdateParams,
    IStopListItem,
    IStopListParams
} from "./MenuApiTypes"

export const menuApi = createApi({
    reducerPath: "menuApi",
    baseQuery,
    tagTypes: ["menu", "categoryRecommendations"],
    endpoints: build => ({
        getMenu: build.query<IBasicResponse<IMenusResponse>, IMenuParams>({
            query: ({serviceMode}) => ({
                url: "v1/menus",
                method: "GET",
                params: {
                    serviceMode
                },
            }),
            providesTags: ["menu"]
        }),
        getMenuList: build.query<IBasicResponse<IMenusResponse>, void>({
            query: () => ({
                url: "v1/menus/list",
                method: "GET",
            }),
            providesTags: ["menu"]
        }),
        syncMenu: build.mutation<IBasicResponse<void>, void>({
            query: () => ({
                url: "v1/menus/sync",
                method: "POST",
            }),
            invalidatesTags: ["menu"]
        }),
        syncCdpMenu: build.mutation<IBasicResponse<void>, void>({
            query: () => ({
                url: "v1/cdp/menus/sync",
                method: "POST",
            })
        }),
        getCombos: build.query<IBasicResponse<ICombo[]>, IComboListParams>({
            query: ({branch_id, service_mode}) => ({
                url: "v1/combos/list",
                method: "POST",
                body: {
                    service_mode,
                    ...(branch_id ? {branch_id} : {})
                }
            }),
            providesTags: ["menu"]
        }),
        getComboCommonList: build.query<IBasicResponse<IComboCommon[]>, void>({
            query: () => ({
                url: "v1/combos/common-list",
                method: "GET",
            }),
            providesTags: ["menu"]
        }),
        getComboById: build.query<IBasicResponse<ICombo>, IComboByIdParams>({
            query: ({id}) => ({
                url: `v1/combos/${id}`,
                method: "GET",
            }),
            providesTags: ["menu"]
        }),
        syncCombos: build.mutation<IBasicResponse<void>, void>({
            query: () => ({
                url: "v1/combos/sync",
                method: "POST",
            }),
            invalidatesTags: ["menu"]
        }),
        getStopList: build.query<IBasicResponse<IStopListItem[]>, IStopListParams>({
            query: ({branchId}) => ({
                url: "v1/stop-list",
                method: "GET",
                params: {
                    branchId
                },
            }),
            providesTags: ["menu"]
        }),
        getCategoryRecommendations: build.query<IBasicResponse<ICategoryRecommendation[]>, void>({
            query: () => ({
                url: "v1/categories/recommendations",
                method: "GET",
            }),
            providesTags: ["categoryRecommendations"]
        }),
        createCategoryRecommendation: build.mutation<IBasicResponse<number>, ICategoryRecommendationCreateParams>({
            query: (body) => ({
                url: "v1/categories/recommendations",
                method: "POST",
                body
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategoryRecommendation: build.mutation<IBasicResponse<void>, ICategoryRecommendationUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/categories/recommendations/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategoryRecommendationProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, ICategoryRecommendationProductPositionUpdateParams>({
            query: ({recommendation_id, product_id, position}) => ({
                url: `v1/categories/recommendations/${recommendation_id}/products/${product_id}/positions`,
                method: "PATCH",
                body: {position}
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        deleteCategoryRecommendation: build.mutation<IBasicResponse<void>, ICategoryRecommendationDeleteParams>({
            query: ({id}) => ({
                url: `v1/categories/recommendations/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategory: build.mutation<IBasicResponse<void>, ICategoryUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/categories/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateProduct: build.mutation<IBasicResponse<void>, IProductUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/products/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, IProductPositionUpdateParams>({
            query: ({id, position}) => ({
                url: `v1/products/${id}/positions`,
                method: "PATCH",
                body: {position}
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductPacket: build.mutation<IBasicResponse<void>, IProductPacketUpdateParams>({
            query: ({idList}) => ({
                url: "v1/products/packets",
                method: "PATCH",
                params: {
                    idList
                },
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductGiftable: build.mutation<IBasicResponse<void>, IProductGiftableUpdateParams>({
            query: ({idList}) => ({
                url: "v1/products/giftable",
                method: "PATCH",
                params: {
                    idList
                },
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductShipping: build.mutation<IBasicResponse<void>, IProductShippingUpdateParams>({
            query: ({id}) => ({
                url: `v1/products/${id}/shipping`,
                method: "PATCH",
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductServiceFee: build.mutation<IBasicResponse<void>, IProductServiceFeeUpdateParams>({
            query: ({id}) => ({
                url: `v1/products/${id}/service-fee`,
                method: "PATCH",
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductSize: build.mutation<IBasicResponse<void>, IProductSizeUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/product-sizes/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateModifier: build.mutation<IBasicResponse<void>, IModifierUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/modifiers/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateModifierGroup: build.mutation<IBasicResponse<void>, IModifierGroupUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/modifier-groups/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateCombo: build.mutation<IBasicResponse<void>, IComboUpdateParams>({
            query: ({id, ...body}) => ({
                url: `v1/combos/${id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItem: build.mutation<IBasicResponse<void>, IComboItemUpdateParams>({
            query: ({combo_item_id, ...body}) => ({
                url: `v1/combos/items/${combo_item_id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItemProduct: build.mutation<IBasicResponse<void>, IComboItemProductUpdateParams>({
            query: ({combo_item_id, product_id, ...body}) => ({
                url: `v1/combos/items/${combo_item_id}/products/${product_id}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItemProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, IComboItemProductPositionUpdateParams>({
            query: ({combo_item_id, product_id, position}) => ({
                url: `v1/combos/items/${combo_item_id}/products/${product_id}/positions`,
                method: "PATCH",
                body: {position}
            }),
            invalidatesTags: ["menu"]
        })
    })
})

export const {
    useGetMenuQuery,
    useGetMenuListQuery,
    useSyncMenuMutation,
    useSyncCdpMenuMutation,
    useGetCombosQuery,
    useGetComboCommonListQuery,
    useGetComboByIdQuery,
    useLazyGetComboByIdQuery,
    useSyncCombosMutation,
    useGetStopListQuery,
    useGetCategoryRecommendationsQuery,
    useCreateCategoryRecommendationMutation,
    useUpdateCategoryRecommendationMutation,
    useUpdateCategoryRecommendationProductPositionMutation,
    useDeleteCategoryRecommendationMutation,
    useUpdateCategoryMutation,
    useUpdateProductMutation,
    useUpdateProductPositionMutation,
    useUpdateProductPacketMutation,
    useUpdateProductGiftableMutation,
    useUpdateProductShippingMutation,
    useUpdateProductServiceFeeMutation,
    useUpdateProductSizeMutation,
    useUpdateModifierMutation,
    useUpdateModifierGroupMutation,
    useUpdateComboMutation,
    useUpdateComboItemMutation,
    useUpdateComboItemProductMutation,
    useUpdateComboItemProductPositionMutation
} = menuApi
